'use strict';

import React, {Component} from "react";
import PropTypes from 'prop-types';
import {observer} from "mobx-react";

import Spinner from 'react-bootstrap/Spinner';

import Broken from "./Broken";
import Login from "./Login";
import UnknownState from "./UnknownState";
import {States} from "./AppConstants";

/**
 * Returns a sensible default component to render for any state other than States.done
 * In the case of States.done, the method returns the passed in doneComponent
 *
 * Expected properties:
 *
 * "state": State being represented
 * "doneComponentBuilder": A function that will provide the component to return if the state is State.done.
 *                         Only called if the state matches State.done
 *
 * Optional properties:
 * "name": A name to bind to the discovered component, if applicable
 * "pendingComponentBuilder": an optional replacement for the default component returned when the state is State.pending
 *                            Only called if the state matches State.pending
 *                            Can be either string (name of animation type) or function
 *
 * @return A basic component to display information ( errors, a loading icon, etc ) related to the given state,
 *         or the given doneComponent if the state is States.done
 *
 */
class SimpleState extends Component {

    constructor(props) {
        super(props);

        this.props = props;
    }

    render() {
        let {name, state, doneComponentBuilder, pendingComponentBuilder, errorComponentBuilder, ...restProps} = this.props;
        let component;

        switch (state) {
            case States.initial:
                component = <span className="sr-only">Loading...</span>;
                break;
            case States.pending:
                if (pendingComponentBuilder && typeof pendingComponentBuilder === "function") {
                    component = pendingComponentBuilder();
                } else {
                    // pendingComponentBuilder not specified, default to circle spinner
                    component =
                        <div className="text-center my-4">
                            <Spinner animation="border" role="status" className="fs-3" style={{width: "3rem", height: "3rem"}}>
                                <span className="sr-only">Loading...</span>
                            </Spinner>
                        </div>;
                }
                break;
            case States.unauthenticated:
                component = <Login />;
                break;
            case States.error:
                if (errorComponentBuilder && typeof errorComponentBuilder === "function") {
                    component = errorComponentBuilder();
                } else {
                    component = <Broken name={name} />;
                }
                break;
            case States.done:
                component = doneComponentBuilder();
                break;
            default:
                component = <UnknownState name={name} />;
        }

        return component;
    }

}

SimpleState.propTypes = {
    state: PropTypes.string.isRequired,
    doneComponentBuilder: PropTypes.func.isRequired,
    // pendingComponentBuilder can be either string or function
    pendingComponentBuilder: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.string
    ]),
    errorComponentBuilder: PropTypes.func,
    name: PropTypes.string
};

export default observer(SimpleState);
